import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import { SectionCard, BackLink } from '@/components/shared/DashboardUI';
import { UserTypePicker } from '@/components/admin/UserFormFields';
import { formatPhoneNumber, parsePhoneNumber, formatZipCode, formatInAppZone } from '@/lib/formatters';
// Vocabularies and defaults come from the shared pricing engine, never from
// literals typed here: the backend validates against the same lists, and a
// second copy is how a select silently offers a value the API 400s on.
import {
  DEFAULT_FEE_MODEL, DEFAULT_INTERVAL, DEFAULT_PROCESSING_FEE,
  VALID_FEE_MODELS, VALID_INTERVALS,
} from '@/lib/offerPricing';
import '@/styles/authenticated.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminEditUser = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', role: '', company_name: '', phone: '',
    address1: '', address2: '', city: '', state: '', zip: '',
    broker_fee_percentage: 15, broker_fee_cap: '', application_fee: '',
    processing_fee: '',
    default_accrual_interval_months: DEFAULT_INTERVAL,
    default_fees_accrue_interest: true,
    default_fee_model: DEFAULT_FEE_MODEL,
    revenue_share_percentage: ''
  });
  const [errors, setErrors] = useState({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, { credentials: 'include', headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        const found = data.user || data;
        setUser(found);
        setFormData({
          name: found.name || '',
          email: found.email || '',
          role: found.role || '',
          company_name: found.company_name || '',
          phone: found.phone || '',
          address1: found.address1 || '',
          address2: found.address2 || '',
          city: found.city || '',
          state: found.state || '',
          zip: found.zip || '',
          broker_fee_percentage: found.broker_fee_percentage ?? 15,
          broker_fee_cap: found.broker_fee_cap ?? '',
          application_fee: (found.application_fee === null || found.application_fee === undefined) ? '' : found.application_fee,
          // `??` and an explicit !== false, never `||`: a funder may
          // legitimately set 0 or false, and `||` would silently restore the
          // platform default over their choice.
          processing_fee: (found.processing_fee === null || found.processing_fee === undefined) ? '' : found.processing_fee,
          default_accrual_interval_months: found.default_accrual_interval_months ?? DEFAULT_INTERVAL,
          default_fees_accrue_interest: found.default_fees_accrue_interest !== false,
          default_fee_model: found.default_fee_model ?? DEFAULT_FEE_MODEL,
          revenue_share_percentage: (found.revenue_share_percentage === null || found.revenue_share_percentage === undefined) ? '' : found.revenue_share_percentage
        });
      } else {
        toast.error('User not found');
        navigate('/admin/users');
      }
    } catch (error) {
      toast.error('Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (formData.role === 'growth_partner') {
      const share = parseFloat(formData.revenue_share_percentage);
      if (formData.revenue_share_percentage === '' || isNaN(share) || share < 0 || share > 100) {
        newErrors.revenue_share_percentage = 'Must be 0-100';
      }
    }
    if (formData.role === 'funder') {
      const pct = parseFloat(formData.broker_fee_percentage);
      if (isNaN(pct) || pct < 0 || pct > 100) newErrors.broker_fee_percentage = 'Must be 0-100';
      if (formData.broker_fee_cap !== '' && formData.broker_fee_cap !== null) {
        const cap = parseFloat(formData.broker_fee_cap);
        if (isNaN(cap) || cap < 0) newErrors.broker_fee_cap = 'Must be a positive number';
      }
      if (formData.application_fee !== '' && formData.application_fee !== null) {
        const af = parseFloat(formData.application_fee);
        if (isNaN(af) || af < 0) newErrors.application_fee = 'Must be 0 or a positive number';
      }
      if (formData.processing_fee !== '' && formData.processing_fee !== null) {
        const pf = parseFloat(formData.processing_fee);
        if (isNaN(pf) || pf < 0) newErrors.processing_fee = 'Must be 0 or a positive number';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      // Role is deliberately absent. It is shown, not edited: changing a user's
      // type retroactively strands whatever their old role owned — a lawyer's
      // firm link, a funder's fee overrides — with nothing to mark that.
      const updateData = {
        name: formData.name,
        company_name: formData.company_name || null,
        phone: formData.phone || null,
        address1: formData.address1 || null,
        address2: formData.address2 || null,
        city: formData.city || null,
        state: formData.state || null,
        zip: formData.zip || null
      };
      // Rides on this same call — the PUT range guard and revenue_share_changed
      // audit event only fire when this key is present in the body.
      if (formData.role === 'growth_partner') {
        updateData.revenue_share_percentage = parseFloat(formData.revenue_share_percentage);
      }
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update user');
        setSaving(false);
        return;
      }

      // If funder, also update broker fee + application fee settings
      if (formData.role === 'funder') {
        const feeData = {
          broker_fee_percentage: parseFloat(formData.broker_fee_percentage),
          broker_fee_cap: formData.broker_fee_cap === '' || formData.broker_fee_cap === null ? null : parseFloat(formData.broker_fee_cap),
          application_fee: formData.application_fee === '' || formData.application_fee === null ? null : parseFloat(formData.application_fee),
          // Empty clears the override back to the platform default; 0 is a
          // real value (waived) and must survive, so the emptiness test is
          // explicit rather than falsiness.
          processing_fee: formData.processing_fee === '' || formData.processing_fee === null ? null : parseFloat(formData.processing_fee),
          default_accrual_interval_months: parseInt(formData.default_accrual_interval_months, 10),
          // A deliberate `false` has to reach the wire, which is why this is
          // coerced rather than passed through a truthiness filter.
          default_fees_accrue_interest: !!formData.default_fees_accrue_interest,
          default_fee_model: formData.default_fee_model
        };
        await fetch(`${API}/admin/users/${userId}/broker-fee`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify(feeData)
        });
      }

      toast.success('User updated successfully');
      navigate('/admin/users');
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner-border" style={{ color: '#070689' }} role="status" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '2rem 1.5rem' }} data-testid="admin-edit-user-page">
      <BackLink onClick={() => navigate('/admin/users')} label="Back to Users" />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--mcl-text-heading)', margin: 0 }}>Edit User</h1>
          <p style={{ color: 'var(--mcl-text-muted)', margin: '0.25rem 0 0', fontSize: '0.9rem' }}>{user.email}</p>
        </div>
        <button className="mcl-btn mcl-btn-primary" onClick={handleSave} disabled={saving} data-testid="save-user-btn">
          <Save size={16} /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Section: Basic Info */}
      <SectionCard title="Basic Information" className="mb-3">
        <UserTypePicker role={formData.role} locked />
        <div className="mcl-form-row">
          <div className="mcl-form-group">
            <label className="mcl-form-label">Full Name *</label>
            <input className={`mcl-form-input ${errors.name ? 'is-invalid' : ''}`} value={formData.name} onChange={(e) => updateField('name', e.target.value)} data-testid="edit-user-name" />
            {errors.name && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.name}</div>}
          </div>
          <div className="mcl-form-group">
            <label className="mcl-form-label">Email</label>
            <input className="mcl-form-input" value={formData.email} disabled style={{ opacity: 0.6 }} />
          </div>
        </div>
        <div className="mcl-form-row">
          <div className="mcl-form-group">
            <label className="mcl-form-label">Company</label>
            <input className="mcl-form-input" value={formData.company_name} onChange={(e) => updateField('company_name', e.target.value)} data-testid="edit-user-company" placeholder="Company name" />
          </div>
          <div className="mcl-form-group">
            <label className="mcl-form-label">Phone</label>
            <input className="mcl-form-input" value={formatPhoneNumber(formData.phone)} onChange={(e) => updateField('phone', parsePhoneNumber(e.target.value))} data-testid="edit-user-phone" placeholder="(555) 123-4567" />
          </div>
        </div>
      </SectionCard>

      {/* Section: Address */}
      <SectionCard title="Address" className="mb-3">
        <div className="mcl-form-group">
          <label className="mcl-form-label">Address 1</label>
          <input className="mcl-form-input" value={formData.address1} onChange={(e) => updateField('address1', e.target.value)} data-testid="edit-user-address1" />
        </div>
        <div className="mcl-form-group">
          <label className="mcl-form-label">Apt / Suite</label>
          <input className="mcl-form-input" value={formData.address2} onChange={(e) => updateField('address2', e.target.value)} data-testid="edit-user-address2" />
        </div>
        <div className="mcl-form-row" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
          <div className="mcl-form-group">
            <label className="mcl-form-label">City</label>
            <input className="mcl-form-input" value={formData.city} onChange={(e) => updateField('city', e.target.value)} data-testid="edit-user-city" />
          </div>
          <div className="mcl-form-group">
            <label className="mcl-form-label">State</label>
            <input className="mcl-form-input" value={formData.state} onChange={(e) => updateField('state', e.target.value)} data-testid="edit-user-state" />
          </div>
          <div className="mcl-form-group">
            <label className="mcl-form-label">Zip</label>
            <input className="mcl-form-input" inputMode="numeric" value={formData.zip} onChange={(e) => updateField('zip', formatZipCode(e.target.value))} data-testid="edit-user-zip" />
          </div>
        </div>
      </SectionCard>

      {/* Section: Broker Fee + Application Fee (Funders only) */}
      {formData.role === 'funder' && (
        <SectionCard title="Fee Overrides" className="mb-3">
          <p style={{ color: 'var(--mcl-text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Configure broker-fee and application-fee overrides for this funder. Set broker fee to <strong>0%</strong> to disable it entirely. Leave application fee blank to use the platform default ($99).
          </p>
          <div className="mcl-form-row">
            <div className="mcl-form-group">
              <label className="mcl-form-label">Broker Fee (%)</label>
              <input className={`mcl-form-input ${errors.broker_fee_percentage ? 'is-invalid' : ''}`} type="number" min="0" max="100" step="0.5" value={formData.broker_fee_percentage} onChange={(e) => updateField('broker_fee_percentage', e.target.value)} data-testid="edit-broker-fee-pct" />
              {errors.broker_fee_percentage && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.broker_fee_percentage}</div>}
              <div className="mcl-form-hint">0 = no broker fee. Default 15%.</div>
            </div>
            <div className="mcl-form-group">
              <label className="mcl-form-label">Broker Fee Cap ($)</label>
              <input className={`mcl-form-input ${errors.broker_fee_cap ? 'is-invalid' : ''}`} type="number" min="0" step="1" value={formData.broker_fee_cap} onChange={(e) => updateField('broker_fee_cap', e.target.value)} data-testid="edit-broker-fee-cap" placeholder="No Cap (leave empty)" />
              {errors.broker_fee_cap && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.broker_fee_cap}</div>}
              <div className="mcl-form-hint">Leave empty for no cap</div>
            </div>
            <div className="mcl-form-group">
              <label className="mcl-form-label">Application Fee ($)</label>
              <input className={`mcl-form-input ${errors.application_fee ? 'is-invalid' : ''}`} type="number" min="0" step="1" value={formData.application_fee} onChange={(e) => updateField('application_fee', e.target.value)} data-testid="edit-application-fee" placeholder="Default $99 (leave empty)" />
              {errors.application_fee && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.application_fee}</div>}
              <div className="mcl-form-hint">Override per-funder. 0 = waived.</div>
            </div>
          </div>
          {/* The funder's standing pricing defaults. The offer builder seeds
              from these and sets a per-offer override flag when one is
              changed for a single offer. Admin support needs them here for
              the same reason it needs the three fees above: to correct a
              funder's terms without a raw API call. */}
          <div className="mcl-form-row">
            <div className="mcl-form-group">
              <label className="mcl-form-label">Processing Fee ($)</label>
              <input className={`mcl-form-input ${errors.processing_fee ? 'is-invalid' : ''}`} type="number" min="0" step="1" value={formData.processing_fee} onChange={(e) => updateField('processing_fee', e.target.value)} data-testid="edit-processing-fee" placeholder={`Default $${DEFAULT_PROCESSING_FEE} (leave empty)`} />
              {errors.processing_fee && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.processing_fee}</div>}
              <div className="mcl-form-hint">Used when the fee model is flat. 0 = waived.</div>
            </div>
            <div className="mcl-form-group">
              <label className="mcl-form-label">Default Fee Model</label>
              <select className="mcl-form-input" value={formData.default_fee_model} onChange={(e) => updateField('default_fee_model', e.target.value)} data-testid="edit-fee-model">
                {VALID_FEE_MODELS.map((m) => (
                  <option key={m} value={m}>{m === 'itemized' ? 'Itemized (broker + application)' : 'Flat processing fee'}</option>
                ))}
              </select>
              <div className="mcl-form-hint">Which fees this funder charges by default.</div>
            </div>
            <div className="mcl-form-group">
              <label className="mcl-form-label">Default Accrual Interval</label>
              <select className="mcl-form-input" value={formData.default_accrual_interval_months} onChange={(e) => updateField('default_accrual_interval_months', e.target.value)} data-testid="edit-accrual-interval">
                {VALID_INTERVALS.map((m) => (
                  <option key={m} value={m}>{m === 1 ? 'Monthly' : `${m} months`}</option>
                ))}
              </select>
              <div className="mcl-form-hint">How wide each payment window is.</div>
            </div>
          </div>
          <div className="mcl-form-group" data-testid="edit-fees-accrue-group">
            <label className="mcl-checkbox-label">
              <input type="checkbox" checked={formData.default_fees_accrue_interest} onChange={(e) => updateField('default_fees_accrue_interest', e.target.checked)} data-testid="edit-fees-accrue" />
              {' '}Fees accrue interest by default
            </label>
            <div className="mcl-form-hint">
              {formData.default_fees_accrue_interest
                ? 'Interest runs on the advance plus all fees.'
                : 'Fees ride on top, uncompounded.'}
            </div>
          </div>
        </SectionCard>
      )}

      {/* Section: Revenue Share (Growth Partners only) */}
      {formData.role === 'growth_partner' && (
        <SectionCard title="Revenue Share" className="mb-3">
          <p style={{ color: 'var(--mcl-text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            The partner's cut of platform revenue on their referred lawyers.
          </p>
          <div className="mcl-form-group">
            <label className="mcl-form-label">Revenue Share (%)</label>
            <input className={`mcl-form-input ${errors.revenue_share_percentage ? 'is-invalid' : ''}`} type="number" min="0" max="100" step="0.1" value={formData.revenue_share_percentage} onChange={(e) => updateField('revenue_share_percentage', e.target.value)} data-testid="edit-revenue-share-pct" />
            {errors.revenue_share_percentage && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.revenue_share_percentage}</div>}
          </div>
        </SectionCard>
      )}

      {/* Section: Account Status */}
      <SectionCard title="Account Status" className="mb-3">
        <div className="mcl-form-row">
          <div className="mcl-form-group">
            <label className="mcl-form-label">Email Verified</label>
            <div style={{ padding: '0.5rem 0', fontWeight: 500, color: user.email_verified ? 'var(--mcl-success, #16a34a)' : 'var(--mcl-danger)' }}>
              {user.email_verified ? 'Yes' : 'No'}
            </div>
          </div>
          {user.role === 'funder' && (
            <div className="mcl-form-group">
              <label className="mcl-form-label">Funder Approved</label>
              <div style={{ padding: '0.5rem 0', fontWeight: 500, color: user.funder_approved !== false ? 'var(--mcl-success, #16a34a)' : '#d97706' }}>
                {user.funder_approved !== false ? 'Approved' : 'Pending Approval'}
              </div>
            </div>
          )}
          {user.role === 'lawyer' && (
            <div className="mcl-form-group">
              <label className="mcl-form-label">Lawyer Approved</label>
              <div style={{ padding: '0.5rem 0', fontWeight: 500, color: user.lawyer_approved !== false ? 'var(--mcl-success, #16a34a)' : '#d97706' }}>
                {user.lawyer_approved !== false ? 'Approved' : 'Pending Approval'}
              </div>
            </div>
          )}
          <div className="mcl-form-group">
            <label className="mcl-form-label">Joined</label>
            <div style={{ padding: '0.5rem 0', color: 'var(--mcl-text-body)' }}>
              {formatInAppZone(user.created_at, { month: '2-digit', day: '2-digit', year: 'numeric' }) || '-'}
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Bottom save button */}
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
        <button className="mcl-btn mcl-btn-outline" onClick={() => navigate('/admin/users')} data-testid="cancel-edit-btn">Cancel</button>
        <button className="mcl-btn mcl-btn-primary" onClick={handleSave} disabled={saving} data-testid="save-user-bottom-btn">
          <Save size={16} /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default AdminEditUser;
